import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AUTH_STATUS,
  fetchAuthSession,
  keepSessionAlive,
  LOADING_AUTH_SESSION,
} from '../../utils/serviceCalls/account';
import { useFeatureFlags } from '../FeatureFlag/FeatureFlagsProvider';

/** @typedef {import('../../utils/serviceCalls/account').AuthSession} AuthSession */
/** @typedef {import('../../utils/serviceCalls/account').AuthUser} AuthUser */
/** @typedef {import('../../utils/serviceCalls/account').UserCategory} UserCategory */

const AuthContext =
  /** @type {import('react').Context<AuthSession | undefined>} */ (
    createContext(undefined)
  );

/** @returns {AuthSession} */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook that throws if user is not authenticated.
 * Use this in protected routes where authentication is guaranteed.
 * @returns {AuthUser & { userCategory: UserCategory }}
 */
export const useAuthenticatedUser = () => {
  const { status, userCategory, user } = useAuth();

  const authenticatedUser = useMemo(
    () => (user ? { ...user, userCategory } : null),
    [user, userCategory],
  );

  // Throw if not authenticated
  if (status !== AUTH_STATUS.AUTHENTICATED || !authenticatedUser) {
    throw new Error(
      'useAuthenticatedUser can only be used in authenticated contexts',
    );
  }

  return authenticatedUser;
};

/**
 * @param {{
 *   children: React.ReactNode;
 *   initialAuthState?: AuthSession;
 * }} props
 */
const AuthProvider = ({ children, initialAuthState }) => {
  const { DisableAuth } = useFeatureFlags();
  const [authState, setAuthState] = useState(
    /** @type {AuthSession} */ (initialAuthState || LOADING_AUTH_SESSION),
  );

  const isAuthenticated = authState.status === AUTH_STATUS.AUTHENTICATED;

  useEffect(() => {
    // Skip fetching if we have an initial auth state (for testing)
    if (initialAuthState) {
      return;
    }

    fetchAuthSession({ useFakeAuth: DisableAuth }).then((authState) => {
      setAuthState(authState);
    });
  }, [initialAuthState, DisableAuth]);

  // Keep session alive for authenticated users
  useEffect(
    function keepAuthenticatedUserSessionAlive() {
      // Only set up keep-alive if user is authenticated
      if (!isAuthenticated) {
        return;
      }

      // Run keep-alive every 19 minutes
      const sessionKeepAliveInterval = setInterval(
        keepSessionAlive,
        19 * 60 * 1000,
      );

      // Call keep-alive immediately
      keepSessionAlive();

      // Stop keep-alive calls on unmount
      return () => {
        clearInterval(sessionKeepAliveInterval);
      };
    },
    [isAuthenticated],
  );

  return (
    <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
